<template>
    <div class="editor-layout-wrapper">
        <div class="editor-layout-menu-lateral">
            <AbaMenuLateral :tabs="tabs" />
        </div>

        <div class="editor-sections-outer" @click="selecionarSecao(null)">
            <div class="loading-overlay" v-if="statusCarregamentoEditorLandingPage === STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.CARREGANDO">
                <ProgressSpinner style="width: 64px; height: 64px" strokeWidth="4" />
            </div>
            <div class="editor-sections-inner">
                <component
                    :is="{ corpo: 'main', cabecalho: 'header', rodape: 'footer' }[secao.componente]"
                    @click.stop="lidarComClickSecao(secao)"
                    v-for="secao in secoesPagina"
                    v-bind="secao.props"
                    :key="secao.componenteId"
                    class="secao-soltar relative"
                    :class="{
                        'secao-cabecalho': secao.componente === 'cabecalho',
                        'secao-corpo': secao.componente === 'corpo',
                        'secao-rodape': secao.componente === 'rodape',
                        'secao-soltar--selecionada': idElementoSelecionado === secao.componenteId,
                    }"
                    :style="{
                        ...secao.props,
                        marginLeft: secao?.props?.marginX,
                        marginRight: secao?.props?.marginX,
                    }"
                >
                    <h4 class="absolute secao-header text-primary flex align-items-center" style="gap: 10px">
                        {{ secao.props.titulo || secao.componente }}
                        <span
                            v-if="secao.componente === 'cabecalho' || secao.componente === 'rodape'"
                            class="material-symbols-outlined select-none cursor-pointer"
                            @click.stop="selecionarElemento(secao.componenteId)"
                            style="font-size: 18px"
                        >
                            edit_square
                        </span>
                    </h4>
                    <span
                        v-if="
                            (secao.componente === 'rodape' || secao.componente === 'cabecalho') &&
                            secao?.props?.style?.backgroundColor == null &&
                            secao?.props?.style?.backgroundImage == null
                        "
                        class="material-symbols-outlined text-primary opacity-25 select-none secao-vazia-placeholder"
                        style="font-size: 36px"
                    >
                        add_photo_alternate
                    </span>

                    <Dropzone
                        v-if="secao.componente === 'corpo'"
                        :componenteId="secao.componenteId"
                        dropzone-class="corpo-dropzone"
                        placeholder-class="zona-soltar-vazia text-primary select-none"
                        :dropzoneStyle="{ padding: '12px', height: '100%' }"
                    >
                        <template #placeholder>
                            <span class="material-symbols-outlined opacity-25" style="font-size: 36px">swipe_right</span>
                            <span class="opacity-60">Arraste os elementos para essa área e solte</span>
                        </template>
                    </Dropzone>
                </component>
            </div>
        </div>

        <div class="editor-layout-config">
            <div class="editor-layout-conteudo">
                <DesignPagina />
                <AbaMenuConfig v-if="idElementoSelecionado != null" />
            </div>
        </div>
    </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useEditorLandingPage } from '@/composables/useEditorLandingPage';
import { LAYOUTS_DISPONIVEIS, COMPONENTES_DISPONIVEIS } from '@/constants/componentesCatalogo';
import { STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM } from '@/store/landingPage';
import ProgressSpinner from 'primevue/progressspinner';
import AbaMenuLateral from '@/components/editorLandingPage/core/AbaMenuLateral.vue';
import AbaMenuConfig from '@/components/editorLandingPage/core/menuConfig/AbaMenuConfig.vue';
import Dropzone from '@/components/editorLandingPage/core/Dropzone.vue';
import DesignPagina from '@/components/editorLandingPage/core/DesignPagina.vue';

const store = useStore();
const route = useRoute();
const { selecionarElemento, idElementoSelecionado, inicializarEstrutura, statusCarregamentoEditorLandingPage } = useEditorLandingPage();

const secoesPagina = computed(() => store.state.landingPage.estruturaPagina.children[0]?.children || []);

const tabs = [
    { header: 'Layouts', list: LAYOUTS_DISPONIVEIS },
    { header: 'Componentes', list: COMPONENTES_DISPONIVEIS },
];

const lidarComClickSecao = (secao) => {
    if (secao.componente === 'corpo') {
        return;
    }
    selecionarElemento(secao.componenteId);
};

onMounted(async () => {
    inicializarEstrutura({ eventoId: route.params.eventoId, landingPageId: route.params.landingPageId });
});

const showEditorScroll = computed(() =>
    statusCarregamentoEditorLandingPage.value === STATUS_CARREGAMENTO_EDITOR_LANDING_PAGE_ENUM.CARREGANDO ? 'hidden' : 'auto'
);
</script>

<style lang="scss" scoped>
.loading-overlay {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    backdrop-filter: blur(2px);
    background-color: rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

.editor-layout-wrapper {
    display: flex;
    overflow: hidden;
    flex-grow: 1;
}

.editor-layout-menu-lateral,
.editor-layout-config {
    position: relative;
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.editor-layout-conteudo {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    overflow-y: auto;
}

.editor-sections-outer {
    position: relative;
    flex: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: #fff;
    border-left: 1px solid #cccccc;
    border-right: 1px solid #cccccc;
    overflow-y: v-bind(showEditorScroll);
}

:deep(.ql-align-right) {
    text-align: right;
}

:deep(.ql-align-justify) {
    text-align: justify;
}

:deep(.ql-align-center) {
    text-align: center;
}

:deep(.corpo-dropzone) {
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    min-height: 300px;
}

:deep(.zona-soltar-vazia) {
    text-align: center;
    color: #6c757d;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 8px;
}

.editor-sections-inner {
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 48px;
}

.secao-cabecalho {
    min-height: 200px;
    max-height: 180px;
    height: 160px;
    cursor: pointer;
}

.secao-corpo {
    padding-top: 24px;
}

.secao-rodape {
    min-height: 200px;
    max-height: 140px;
    height: 180px;
    cursor: pointer;
}

.secao-soltar {
    background: #f7f7f7;
    outline: 1px solid #cccccc;
    border-radius: 4px;
    transition: background-color 0.2s ease-in-out, outline 0.2s ease-in-out;
    position: relative;

    h4 {
        margin-top: 0;
        margin-bottom: 10px;
        color: #212529;
        font-size: 1.1rem;
        display: flex;
        box-sizing: border-box;
    }

    &--selecionada {
        outline: 2px dashed #0772b3;
        background-color: #eaf6fb;
    }
}

.secao-vazia-placeholder {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.secao-header {
    border: 1px solid #cccccc;
    border-radius: 2px;
    padding: 4px;
    background: #fff;
    top: 4px;
    left: 4px;
}

.opacity-25 {
    opacity: 0.25;
}
</style>
